// SPDX-License-Identifier: LicenseRef-Proprietary

import React, { useState, useRef, useEffect } from "react";

interface CitationFieldProps {
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

/**
 * Citation input field with improved display
 * Shows sources in a compact, readable format with truncation for long text
 */
export const CitationField: React.FC<CitationFieldProps> = ({
  value = "",
  onChange,
  placeholder = "",
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isExpanded && containerRef.current && popoverRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const minPopoverWidth = 400;
      const maxPopoverWidth = 600;
      const padding = 16; // Viewport padding
      
      // Calculate max width for each alignment
      // When right: 0, popover extends LEFT from container's right edge
      // Max width = distance from container's right edge to viewport's left edge (with padding)
      const maxWidthRight = containerRect.right - padding;
      
      // When left: 0, popover extends RIGHT from container's left edge
      // Max width = distance from container's left edge to viewport's right edge (with padding)
      const maxWidthLeft = viewportWidth - containerRect.left - padding;
      
      // Determine optimal width and alignment
      let popoverWidth: number;
      let alignment: { left?: number; right?: number };
      
      // Constrain to max popover width
      const constrainedWidthRight = Math.min(maxPopoverWidth, maxWidthRight);
      const constrainedWidthLeft = Math.min(maxPopoverWidth, maxWidthLeft);
      
      // Prefer right alignment if it gives us more or equal space and meets minimum
      if (constrainedWidthRight >= minPopoverWidth && constrainedWidthRight >= constrainedWidthLeft) {
        popoverWidth = constrainedWidthRight;
        alignment = { right: 0 };
      } else if (constrainedWidthLeft >= minPopoverWidth) {
        // Use left alignment if it gives us more space
        popoverWidth = constrainedWidthLeft;
        alignment = { left: 0 };
      } else {
        // Not enough space for min width on either side
        // Use whichever side has more space, but constrain to actual available space
        if (maxWidthRight >= maxWidthLeft) {
          popoverWidth = Math.min(minPopoverWidth, maxWidthRight);
          alignment = { right: 0 };
        } else {
          popoverWidth = Math.min(minPopoverWidth, maxWidthLeft);
          alignment = { left: 0 };
        }
      }
      
      setPopoverStyle({ 
        ...alignment, 
        width: `${popoverWidth}px`,
        maxWidth: `${popoverWidth}px`
      });
    }
  }, [isExpanded]);

  return (
    <div ref={containerRef} className="citation-field" style={{ position: 'relative', width: '100%', minWidth: 0 }}>
      {value ? (
        <div className="citation-field__display" style={{ 
          display: 'flex', 
          alignItems: 'center',
          gap: '6px',
          fontSize: '11px',
          color: '#94a3b8',
          width: '100%',
          minWidth: 0
        }}>
          <span 
            style={{ 
              fontStyle: 'italic', 
              color: '#cbd5e1',
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              cursor: 'help'
            }}
            title={value}
          >
            {value}
          </span>
          
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            style={{
              fontSize: '10px',
              color: '#3b82f6',
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              textDecoration: 'underline',
              flexShrink: 0
            }}
            title="Edit source"
          >
            Edit
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          style={{
            fontSize: '11px',
            color: '#64748b',
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '2px'
          }}
          title="Add citation/source"
        >Add Source
        </button>
      )}
      {isExpanded && (
        <div 
          ref={popoverRef}
          className="citation-field__popover" 
          style={{ 
            position: 'absolute', 
            top: '100%', 
            ...popoverStyle,
            zIndex: 50,
            marginTop: '4px',
            backgroundColor: '#1e293b',
            border: '1px solid #334155',
            borderRadius: '4px',
            padding: '8px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
          }}>
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="citation-field__input"
            autoFocus
            style={{
              width: '100%',
              padding: '6px 10px',
              fontSize: '12px',
              borderRadius: '4px',
              border: '1px solid #334155',
              backgroundColor: '#0f172a',
              color: '#f1f5f9',
              marginBottom: '4px',
              boxSizing: 'border-box'
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={() => setIsExpanded(false)}
              style={{
                fontSize: '11px',
                color: '#94a3b8',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '2px 4px'
              }}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
